import React, { FC } from 'react';
import { useModal, IsInsideModalContext } from 'store/modal';
import Modal from 'react-bootstrap/Modal';
import { BsExclamationCircle, BsCheckCircle, BsQuestionCircle, BsCardList } from 'react-icons/bs';
import { FaPrint } from 'react-icons/fa';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import Spinner from './Spinner';

const ModalComponent: FC = () => {
  const { t } = useTranslation();
  const { hideModal, isVisible, props } = useModal();
  const {
    type,
    size = 'sm',
    heading,
    content,
    links,
    onConfirm,
    confirmLabel,
    confirmBtnType,
    confirmButton,
    onCancel,
    cancelLabel,
    onSuccess,
    onLinkClick,
    successButton,
    hideIcon,
  } = props;

  const buildIcon = () => {
    if (hideIcon) {
      return;
    }
    if (type === 'confirm')
      return (
        <BsQuestionCircle
          size={70}
          color="#FFB822"
          style={{ marginTop: '0.5rem', marginBottom: 20, opacity: 0.7 }}
        />
      );

    if (type === 'error')
      return (
        <BsExclamationCircle
          size={70}
          color="#ef476f"
          style={{ marginTop: '0.5rem', marginBottom: 20, opacity: 0.7 }}
        />
      );

    if (type === 'loading')
      return (
        <Spinner
          size={50}
          color="#ced4da"
          style={{ marginTop: '0.5rem', marginBottom: 20, opacity: 0.7 }}
        />
      );

    if (type === 'form' && !hideIcon)
      return (
        <BsQuestionCircle
          size={70}
          color="#ced4da"
          style={{ marginTop: '0.5rem', marginBottom: 20, opacity: 0.7 }}
        />
      );

    if (type === 'list')
      return (
        <BsCardList
          size={70}
          color="#32bfa3"
          style={{ marginTop: '0.5rem', marginBottom: 20, opacity: 0.7 }}
        />
      );

    if (type === 'info') return <div className="m-3" />;

    return (
      <BsCheckCircle
        size={80}
        color="#32bfa3"
        style={{ marginBottom: 20, opacity: 0.7 }}
      />
    );
  };

  const buildTitle = () => {
    if (type === 'error') return t('common.error');

    if (type === 'success') return t('common.success');

    if (type === 'loading') return t('common.loading');

    return null;
  };

  return (
    <Modal
      centered
      size={size}
      show={isVisible}
      onHide={() => {
        hideModal();
        if (onSuccess) onSuccess();
      }}
    >
      <ModalHeader closeButton />
      <ModalBody>
        {buildIcon()}
        <h2 className="mb-3">{heading || buildTitle()}</h2>
        {type === 'list' && links && (
          <div className="mb-3">
            <ol>
              {links.map((element, index) => (
                <li
                  key={index}
                  style={{ textAlign: 'left' }}
                >
                  <Button
                    ariaLabel="download link"
                    btnType="link"
                    outlined
                    className="ml-10"
                    size="lg"
                    onClick={() => {
                      if (onLinkClick) {
                        onLinkClick(element.id);
                      }
                    }}
                  >
                    {element.fileName}
                    <FaPrint
                      size={16}
                      style={{
                        marginLeft: '0.5rem',
                      }}
                    />
                  </Button>
                </li>
              ))}
            </ol>
          </div>
        )}
        {content && (
          <IsInsideModalContext.Provider value>
            {typeof content === 'string' ? <p>{content}</p> : <div className="mb-3">{content}</div>}
          </IsInsideModalContext.Provider>
        )}
        {type === 'confirm' && (
          <div>
            {!confirmButton && (
              <Button
                className="confirm-btn"
                testId="modal-confirm-button"
                btnType={confirmBtnType || 'primary'}
                onClick={() => {
                  hideModal();
                  if (onConfirm) onConfirm();
                }}
              >
                {confirmLabel || t('common.confirm')}
              </Button>
            )}
            {confirmButton}
            <Button
              btnType="light"
              outlined
              className="ml-2"
              onClick={() => {
                hideModal();
                if (onCancel) onCancel();
              }}
            >
              {cancelLabel || t('common.cancel')}
            </Button>
          </div>
        )}
        {(type === 'success' || type === 'error' || type === 'info') && !successButton && (
          <Button
            btnType="primary"
            onClick={() => {
              hideModal();
              if (onSuccess) onSuccess();
            }}
          >
            {t('common.ok')}
          </Button>
        )}
        {(type === 'success' || type === 'error') && successButton && <div>{successButton}</div>}
        {type === 'loading' && (
          <Button
            btnType="danger"
            onClick={hideModal}
          >
            {t('common.cancel')}
          </Button>
        )}
      </ModalBody>
    </Modal>
  );
};

const ModalHeader = styled(Modal.Header)`
  padding: 0;
  border: 0;

  .close {
    position: absolute;
    right: 1rem;
    top: 0.5rem;
    z-index: 10;
  }
`;

const ModalBody = styled(Modal.Body)`
  text-align: center;
  margin-bottom: 0.75rem;
  padding: 1rem 1.25rem;
`;

export default ModalComponent;
